import { useCallback, useEffect, useRef, useState } from 'react';

import { submitFeedback, SubmitFeedbackArgs } from '../api/feedback';

// Posts a single rating to the AI service. Plain-fetch-in-hook, matching useChat:
// local isSubmitting/error state, a mountedRef so a resolved POST can't setState on
// an unmounted drawer. `submit` resolves to a boolean so the widget only shows its
// "Thanks" state once the write actually succeeded.
export function useFeedback() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const submit = useCallback(async (args: SubmitFeedbackArgs): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await submitFeedback(args);
      return true;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to submit feedback');
      }
      return false;
    } finally {
      if (mountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, []);

  return { submitFeedback: submit, isSubmitting, error };
}
